<template>
  <div>
    <div v-if="isLoading" class="p-4"><b-spinner /></div>
    <div v-else-if="!params.pinned" class="p-4" style="display: flex; flex-direction: column; gap: 16px">
      <div class="d-flex" style="gap: 4px" v-if="teilnehmerNew?.length > 1">
        <b-button class="btn-icon icon-button" @click="expandAll">
          <Maximize2 :size="20" />
        </b-button>
        <b-button class="btn-icon icon-button" @click="collapseAll">
          <Minimize2 :size="20" />
        </b-button>
      </div>

      <div v-for="teilnehmer in teilnehmerNew" :key="teilnehmer.name + ' ' + teilnehmer.nummer">
        <div
          class="d-flex align-items-center cursor-pointer mb-1 p-4"
          style="
            gap: 8px;
            background-color: #e5f0fb;
            filter: drop-shadow(0 3px 3px rgb(0 0 0 / 0.12));
            border-radius: 0.375rem;
            align-items: center;
          "
          @click="expandTeilnehmer(teilnehmer.nummer)"
        >
          <i v-if="teilnehmer.expanded" class="fa-chevron-down fas"></i>
          <i v-else class="fa-chevron-up fas"></i>

          <h5 class="mb-0">{{ nameFormatter(teilnehmer.name) }}</h5>
          <div style="display: flex; align-items: center; gap: 8px; height: 20px">
            <span v-if="teilnehmer.statusPruefung === 'AllGood'">
              <i class="fas fa-plane fa-lg text-success"></i>
            </span>
            <span v-else-if="teilnehmer.statusPruefung === 'SomethingWrong'">
              <i class="fas fa-plane fa-lg text-warning"></i>
            </span>
            <span v-else-if="teilnehmer.statusPruefung === 'NoSegment'">
              <i class="fas fa-plane fa-lg text-danger"></i>
            </span>
            <i v-if="teilnehmer.statusTickets === 'AllGood'" class="fas fa-ticket-alt text-success fa-lg"></i>
            <i
              v-else-if="teilnehmer.statusTickets === 'CheckRequired'"
              class="fas fa-ticket-alt text-warning fa-lg"
            ></i>
            <i v-else class="fas fa-ticket-alt text-danger fa-lg"></i>
          </div>
          <b-badge
            v-if="passenderNameWennKeinerGefunden(teilnehmer)"
            :id="'tooltip-target-name-falsch' + teilnehmer.name"
            variant="danger"
          >
            Namensabweichung (?)
          </b-badge>
          <b-tooltip
            :target="'tooltip-target-name-falsch' + teilnehmer.name"
            triggers="hover"
            custom-class="custom-tooltip"
          >
            <div>
              <div>Passagier Name:</div>
              <b>{{ passenderNameWennKeinerGefunden(teilnehmer) }}</b>
            </div>
          </b-tooltip>
        </div>
        <div
          v-if="teilnehmer.expanded"
          class="card"
          style="filter: drop-shadow(0 3px 3px rgb(0 0 0 / 0.12)); border-radius: 0.375rem"
        >
          <TeilnehmerReiseterminFlugstrecken :teilnehmer="teilnehmer" />
        </div>
      </div>

      <div v-if="nichtZugeordneteSegmente.length > 0">
        <div
          class="d-flex align-items-center cursor-pointer mb-1 p-4"
          style="
            gap: 8px;
            background-color: #e5f0fb;
            filter: drop-shadow(0 3px 3px rgb(0 0 0 / 0.12));
            border-radius: 0.375rem;
          "
        >
          <h5 class="m-0">Nicht zugeordnete Segmente</h5>
        </div>
        <div class="p-4 m-2 card">
          <h6>Alle Passagiere: {{ passagierNamen }}</h6>
          <AgGridVue
            class="ag-theme-alpine m-0 p-0"
            :columnDefs="nichtZugeordneteSegmenteFields"
            :rowData="nichtZugeordneteSegmente"
            :rowHeight="'40px'"
            :headerHeight="'40px'"
            :style="{
              height: (nichtZugeordneteSegmente.length + 1) * 40 + 18 + 'px',
              boxSizing: 'content-box',
            }"
            :defaultColDef="{ sortable: false, resizable: false }"
          ></AgGridVue>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { AgGridVue } from 'ag-grid-vue';
import { format, isBefore } from 'date-fns';
import * as odataService from '@/core/common/services/odata.service';
import TeilnehmerReiseterminFlugstrecken from './teilnehmer-reisetermin-flugstrecken.vue';
import { normalizeNameForComparison, levenshteinSimilarity } from '@/core/common/helpers/utils.js';
import { Maximize2, Minimize2 } from 'lucide-vue';

export default {
  components: { AgGridVue, TeilnehmerReiseterminFlugstrecken, Maximize2, Minimize2 },
  data() {
    return {
      isLoading: false,
      data: null,
      teilnehmers: null,
      teilnehmerNew: [],
      nichtZugeordneteSegmente: [],
      passagierNamen: '',
      normalizedPassagierNamen: [],
      statusConverter: {
        AllGood: '<i class="fas fa-check text-success fa-lg"></i>',
        SomethingWrong: '<i class="fas fa-exclamation text-warning fa-lg"></i>',
        NoSegment: '<i class="fas fa-times text-danger fa-lg"></i>',
      },
      statusTicketsConverter: {
        AllGood: '<i class="fas fa-ticket-alt text-success fa-lg"></i>',
        CheckRequired: '<i class="fas fa-ticket-alt text-warning fa-lg"></i>',
        Critical: '<i class="fas fa-ticket-alt text-danger fa-lg"></i>',
      },
      hauptstreckeFields: [
        {
          field: 'hinRueck',
          headerName: 'Hin/Rück',
          width: 120,
        },
        {
          field: 'abflughafen',
          headerName: 'Abflugh.',
          width: 100,
        },
        {
          field: 'zielflughafen',
          headerName: 'Zielflugh.',
          width: 100,
        },
        {
          field: 'fruehesteAnkunft',
          headerName: 'Früh. Ankunft',
          width: 140,
          cellRenderer: params =>
            params.value ? format(new Date(params.value.substring(0, 16)), 'dd.MM.yyyy HH:mm') : null,
        },
        {
          field: 'spaetesteAnkunft',
          headerName: 'Spät. Ankunft',
          width: 140,
          cellRenderer: params =>
            params.value ? format(new Date(params.value.substring(0, 16)), 'dd.MM.yyyy HH:mm') : null,
        },
        {
          field: 'fruehesterAbflug',
          headerName: 'Früh. Abflug',
          width: 140,
          cellRenderer: params =>
            params.value ? format(new Date(params.value.substring(0, 16)), 'dd.MM.yyyy HH:mm') : null,
        },
        {
          field: 'spaetesterAbflug',
          headerName: 'Spät. Abflug',
          width: 140,
          cellRenderer: params =>
            params.value ? format(new Date(params.value.substring(0, 16)), 'dd.MM.yyyy HH:mm') : null,
        },
        {
          field: 'statusPruefung',
          headerName: 'Status Prüfung',
          width: 120,
          cellRenderer: params => this.statusConverter[params.value],
        },
        {
          field: 'statusTickets',
          headerName: 'Status Tickets',
          width: 110,
          cellRenderer: params => (params.value == null ? null : this.statusTicketsConverter[params.value]),
        },
        {
          field: 'strecke',
          headerName: 'Strecke',
          width: 110,
        },
        {
          field: 'betreiberUndFlugnummer',
          headerName: 'Flugnr. Ist',
          width: 110,
        },
        {
          field: 'abflug',
          headerName: 'Abflug',
          width: 150,
          cellRenderer: params =>
            params.value ? format(new Date(params.value.substring(0, 16)), 'dd.MM.yyyy HH:mm') : null,
          cellStyle: params => {
            const { abflug, fruehesterAbflug, spaetesterAbflug, hinRueck } = params.data;
            if (hinRueck === 'Rück') {
              if (
                isBefore(new Date(abflug), new Date(fruehesterAbflug)) ||
                isBefore(new Date(spaetesterAbflug), new Date(abflug))
              ) {
                return { backgroundColor: '#fc620350' };
              }
            }
          },
        },
        {
          field: 'ankunft',
          headerName: 'Ankunft',
          width: 150,
          cellRenderer: params =>
            params.value ? format(new Date(params.value.substring(0, 16)), 'dd.MM.yyyy HH:mm') : null,
          cellStyle: params => {
            const { ankunft, fruehesteAnkunft, spaetesteAnkunft, hinRueck } = params.data;

            if (hinRueck === 'Hin') {
              if (
                isBefore(new Date(ankunft), new Date(fruehesteAnkunft)) ||
                isBefore(new Date(spaetesteAnkunft), new Date(ankunft))
              ) {
                return { backgroundColor: '#fc620350' };
              }
            }
          },
        },
        {
          field: 'ticketAusgestellt',
          headerName: 'Ticket',
          width: 80,
          cellRenderer: params => {
            return params.value == null
              ? null
              : params.value
              ? "<i class='fas fa-check text-success fa-lg'></i>"
              : "<i class='ml-1 fas fa-times text-danger fa-lg'></i>";
          },
        },
      ],
      inlandsfluegeFields: [
        {
          field: 'streckeInlandsflug',
          headerName: 'Strecke',
          width: 90,
        },
        {
          field: 'flugnummer',
          headerName: 'Flugnr. Soll',
          width: 110,
        },
        {
          field: 'fruehesterAbflug',
          headerName: 'Früh. Abflug',
          width: 140,
          cellRenderer: params =>
            params.value ? format(new Date(params.value.substring(0, 16)), 'dd.MM.yyyy HH:mm') : null,
        },
        {
          field: 'spaetesteAnkunft',
          headerName: 'Spät. Ankunft',
          width: 140,
          cellRenderer: params =>
            params.value ? format(new Date(params.value.substring(0, 16)), 'dd.MM.yyyy HH:mm') : null,
        },
        {
          field: 'minimaleUmsteigezeit',
          headerName: 'Min. Umsteigezeit',
          width: 130,
        },
        {
          field: 'statusPruefung',
          headerName: 'Status Prüfung',
          width: 120,
          cellRenderer: params => this.statusConverter[params.value],
        },
        {
          field: 'statusTickets',
          headerName: 'Status Tickets',
          width: 110,
          cellRenderer: params => (params.value == null ? null : this.statusTicketsConverter[params.value]),
        },
        {
          field: 'streckeSegment',
          headerName: 'Strecke',
          width: 110,
          cellStyle: params => {
            if (params.value == null) {
              return { backgroundColor: '#fc620350' };
            }
          },
        },
        {
          field: 'flugnummerSegment',
          headerName: 'Flugnr. Segment',
          width: 130,
          cellStyle: params => {
            if (params.value == null || params.value != params.data.flugnummer) {
              return { backgroundColor: '#fc620350' };
            }
          },
        },
        {
          field: 'abflug',
          headerName: 'Abflug',
          width: 150,
          cellRenderer: params => (params.value ? format(new Date(params.value), 'dd.MM.yyyy HH:mm') : null),
          cellStyle: params => {
            if (params.value == null) {
              return { backgroundColor: '#fc620350' };
            }
          },
        },
        {
          field: 'ankunft',
          headerName: 'Ankunft',
          width: 150,
          cellRenderer: params => (params.value ? format(new Date(params.value), 'dd.MM.yyyy HH:mm') : null),
          cellStyle: params => {
            if (params.value == null) {
              return { backgroundColor: '#fc620350' };
            }
          },
        },
      ],
      nichtZugeordneteSegmenteFields: [
        {
          field: 'strecke',
          headerName: 'Strecke',
          cellRenderer: params => params.data.abflughafen + '-' + params.data.zielflughafen,
        },
        { field: 'betreiber', headerName: 'Betreiber' },
        { field: 'flugnummer', headerName: 'Flugnummer' },
        {
          field: 'abflugsdatum',
          headerName: 'Abflug',
          cellRenderer: params =>
            params.value ? format(new Date(params.value.substring(0, 16)), 'dd.MM.yyyy HH:mm') : null,
        },
        {
          field: 'ankunftsdatum',
          headerName: 'Ankunft',
          cellRenderer: params =>
            params.value ? format(new Date(params.value.substring(0, 16)), 'dd.MM.yyyy HH:mm') : null,
        },
        {
          field: 'ticketAusgestellt',
          headerName: 'Ticket ausgestellt',
          cellRenderer: params => {
            return params.data.buchungsstatus == null
              ? null
              : params.data.buchungsstatus === 'TicketIssued'
              ? "<i class='fas fa-check text-success fa-lg'></i>"
              : "<i class='ml-1 fas fa-times text-danger fa-lg'></i>";
          },
        },
        {
          field: 'segmentStatus',
          headerName: 'Segmentstatus',
        },
        {
          field: 'ticketReallyExists',
          headerName: 'Ticket vorhanden?',
          cellRenderer: params => {
            return params.value
              ? "<i class='fas fa-check text-success fa-lg'></i>"
              : "<i class='ml-1 fas fa-times text-danger fa-lg'></i>";
          },
        },
        {
          field: 'segmentNummer',
          headerName: 'Nummer',
        },
      ],
    };
  },
  mounted() {
    this.isLoading = true;
    odataService
      .getVorgang({
        filter: { id: this.params.data.id },
        select: ['id'],
        expand: {
          flugbuchungen: {
            filter: { isDeleted: false },
            expand: {
              segmente: { filter: { isDeleted: false } },
              passagiere: { filter: { isDeleted: false } },
              tickets: {
                filter: { isDeleted: false },
                select: ['ticketNummer', 'passagierId'],
                expand: { segments: { select: ['segmentNummer'] } },
              },
            },
          },
          teilnehmers: {
            select: ['name', 'nummer', 'eigenanreiseart'],
            filter: { deletedAt: null },
            expand: {
              verlaengerungen: {
                filter: { deletedAt: null },
              },
              teilnehmerVerlaengerungen: { filter: { deletedAt: null } },
              teilnehmerReiseterminFlugstrecken: {
                expand: {
                  segmente: {},
                  reiseterminFlugstrecke: {},
                },
              },
            },
          },
        },
      })
      .then(res => {
        this.isLoading = false;
        const v = res.data[0];
        this.data = v;
        this.teilnehmerNew = this.data.teilnehmers.map((t, index) => ({
          ...t,
          expanded: index === 0,
          statusPruefung:
            t.teilnehmerReiseterminFlugstrecken.length == 0
              ? 'NoSegment'
              : t.teilnehmerReiseterminFlugstrecken.some(
                  flugstrecke =>
                    flugstrecke.statusPruefung != 'SomethingWrong' && flugstrecke.statusPruefung != 'AllGood'
                )
              ? 'NoSegment'
              : t.teilnehmerReiseterminFlugstrecken.some(
                  flugstrecke => flugstrecke.statusPruefung == 'SomethingWrong'
                )
              ? 'SomethingWrong'
              : 'AllGood',
          statusTickets:
            t.teilnehmerReiseterminFlugstrecken.length == 0
              ? 'Critical'
              : t.teilnehmerReiseterminFlugstrecken.some(fs => fs.statusTickets === 'Critical')
              ? 'Critical'
              : t.teilnehmerReiseterminFlugstrecken.some(fs => fs.statusTickets === 'CheckRequired')
              ? 'CheckRequired'
              : 'AllGood',
        }));

        const segmentKeys = this.data.teilnehmers.flatMap(t =>
          t.teilnehmerReiseterminFlugstrecken.flatMap(strecke =>
            strecke.segmente.map(({ flugnummer, segmentNummer }) => `${flugnummer}_${segmentNummer}`)
          )
        );
        const uniqueSegmentKeys = new Set(segmentKeys);

        this.passagierNamen = this.data.flugbuchungen
          .flatMap(f => f.passagiere)
          .map(t => t.vorname + ' ' + t.nachname)
          .join(', ');

        this.normalizedPassagierNamen = this.data.flugbuchungen
          .flatMap(f => f.passagiere)
          .map(t => ({
            normalizedPassagierName:
              this.normalizeNameForComparison(t.vorname) + '' + this.normalizeNameForComparison(t.nachname),
            passagierName: this.nameFormatter(t.vorname) + ' ' + this.nameFormatter(t.nachname),
          }));

        this.nichtZugeordneteSegmente =
          this.data.flugbuchungen?.length > 0
            ? this.data.flugbuchungen
                .flatMap(f => f.segmente)
                .map(s => ({
                  ...s,
                  ticketReallyExists: this.data.flugbuchungen
                    .filter(fb => fb.flugbuchungsId == s.flugbuchungsId)
                    .flatMap(f => f.tickets)
                    .flatMap(t => t.segments)
                    .map(s => s.segmentNummer)
                    .some(nummer => nummer === s.segmentNummer),
                }))
                .filter(s => !uniqueSegmentKeys.has(`${s.flugnummer}_${s.segmentNummer}`))
            : [];
      });
  },
  computed: {},
  methods: {
    nameFormatter(fullName) {
      return fullName
        .replaceAll('.', '')
        .replaceAll(' MRS ', '')
        .replaceAll(' MRS', '')
        .replaceAll(' MR ', '')
        .replaceAll(' MR', '')
        .split(' ')
        .map(name => name.substring(0, 1).toUpperCase() + name.substring(1).toLowerCase())
        .join(' ')
        .split('-')
        .map(name => name.substring(0, 1).toUpperCase() + name.substring(1))
        .join('-');
    },
    normalizeNameForComparison,
    getTeilnehmerNameSimilarities(teilnehmerName) {
      const normalizedTeilnehmerName = normalizeNameForComparison(teilnehmerName);

      return this.normalizedPassagierNamen.map(({ normalizedPassagierName, passagierName }) => ({
        normalizedPassagierName,
        normalizedTeilnehmerName,
        passagierName,
        similarity: levenshteinSimilarity(normalizedTeilnehmerName, normalizedPassagierName).toFixed(2),
      }));
    },
    passenderNameWennKeinerGefunden(teilnehmer) {
      const teilnehmerNameSimilarities = this.getTeilnehmerNameSimilarities(teilnehmer.name);
      console.log(teilnehmerNameSimilarities);
      const einGenauPassenderName = teilnehmerNameSimilarities.some(({ similarity }) => similarity == 100);
      const potenziellMismatchedNamen = teilnehmerNameSimilarities.filter(
        obj => obj.similarity >= 90 && obj.similarity < 100
      );
      const keinSegmentGematcht = teilnehmer.teilnehmerReiseterminFlugstrecken.every(
        fs => fs.segmente.length === 0
      );

      if (einGenauPassenderName || potenziellMismatchedNamen.length != 1 || !keinSegmentGematcht) return null;

      return potenziellMismatchedNamen[0].passagierName;
    },
    expandTeilnehmer(nummer) {
      const teilnehmer = this.teilnehmerNew.find(t => t.nummer === nummer);
      teilnehmer.expanded = !teilnehmer.expanded;
    },
    expandAll() {
      this.teilnehmerNew = this.teilnehmerNew.map(t => ({ ...t, expanded: true }));
    },
    collapseAll() {
      this.teilnehmerNew = this.teilnehmerNew.map(t => ({ ...t, expanded: false }));
    },
  },
};
</script>
<style scoped>
:deep(.ag-cell),
:deep(.ag-header-cell) {
  padding: 0px 0.7rem;
}

.btn.btn-icon.icon-button {
  width: 32px;
  height: 32px;
}
</style>
